import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';
type ThemePreference = 'light' | 'dark' | 'system' | 'auto';

interface ThemeContextType {
  theme: Theme;
  themePreference: ThemePreference;
  setTheme: (theme: Theme) => void;
  setThemePreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
  resetToSystem: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>('light');
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>('system');
  const [mounted, setMounted] = useState(false);

  // Função para detectar preferência do sistema
  const detectSystemTheme = (): Theme => {
    if (typeof window === 'undefined') return 'light';
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    return mediaQuery.matches ? 'dark' : 'light';
  };

  // Função para detectar tema baseado no horário (6h-18h = light, 18h-6h = dark)
  const detectTimeBasedTheme = (): Theme => {
    const hour = new Date().getHours();
    return (hour >= 6 && hour < 18) ? 'light' : 'dark';
  };

  // Função para determinar o tema baseado na preferência
  const getThemeFromPreference = (preference: ThemePreference): Theme => {
    switch (preference) {
      case 'light':
        return 'light';
      case 'dark':
        return 'dark';
      case 'system':
        return detectSystemTheme();
      case 'auto':
        return detectTimeBasedTheme();
      default:
        return 'light';
    }
  };

  // Carregar tema do localStorage na inicialização
  useEffect(() => {
    const savedThemePreference = localStorage.getItem('themePreference') as ThemePreference;
    const savedTheme = localStorage.getItem('theme') as Theme;
    
    if (savedThemePreference && ['light', 'dark', 'system', 'auto'].includes(savedThemePreference)) {
      setThemePreferenceState(savedThemePreference);
      setThemeState(getThemeFromPreference(savedThemePreference));
    } else if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      // Fallback para compatibilidade com versão anterior
      setThemePreferenceState('system');
      setThemeState(savedTheme);
    } else {
      // Padrão: seguir preferência do sistema
      setThemePreferenceState('system');
      setThemeState(detectSystemTheme());
    }
    setMounted(true);
  }, []);

  // Escutar mudanças na preferência do sistema
  useEffect(() => {
    if (!mounted) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Só muda se a preferência for 'system'
      if (themePreference === 'system') {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mounted, themePreference]);

  // Atualizar tema quando a preferência muda
  useEffect(() => {
    if (!mounted) return;
    
    const newTheme = getThemeFromPreference(themePreference);
    setThemeState(newTheme);
  }, [themePreference, mounted]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
    // Quando usuário define tema manualmente, muda preferência para fixo
    const preference = newTheme === 'light' ? 'light' : 'dark';
    setThemePreferenceState(preference);
    localStorage.setItem('themePreference', preference);
  };

  const setThemePreference = (preference: ThemePreference) => {
    setThemePreferenceState(preference);
    localStorage.setItem('themePreference', preference);
    
    const newTheme = getThemeFromPreference(preference);
    setThemeState(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  const resetToSystem = () => {
    setThemePreference('system');
  };

  // Fornecer contexto mesmo durante a hidratação
  const contextValue = { 
    theme, 
    themePreference, 
    setTheme, 
    setThemePreference, 
    toggleTheme, 
    resetToSystem 
  };

  // Aplicar tema ao DOM
  useEffect(() => {
    if (!mounted) return;

    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
    }
  }, [theme, mounted]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};